import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Typography, Button, useStateInRef } from 'seagull';
import { pluralize } from '~/utils/pluralize';
import { SuccessBox, ErrorBox, ImportCard } from './CommonComponents';
import styles from './ImportWizard.module.scss';
import { createRowErrorsObject } from './ImportWizard.utils';
export default function UploadStep(props) {
    var _Object_keys;
    const { importBtnText, entityName, getItemTypeCounts, csvTemplateUrl, testId, onStartImportItems, onEvent } = props;
    const getStateInRef = useStateInRef(props);
    const csvInputRef = useRef(null);
    const [errors, setErrors] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    const [parsedItems, setParsedItems] = useState([]);
    const [showCsvParseResults, setShowCsvParseResults] = useState(false);
    const [isInvalidTemplate, setIsInvalidTemplate] = useState(false);
    const isCsvValid = ()=>csvFile && !(errors || isInvalidTemplate) && (parsedItems === null || parsedItems === void 0 ? void 0 : parsedItems.length) > 0;
    useEffect(()=>{
        setIsInvalidTemplate(false);
        if (!csvFile) {
            setErrors(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e)=>{
            const { parseAndValidateCsv, onUploadCsv } = getStateInRef();
            try {
                var _e_target;
                const data = (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.result;
                if (!data) return;
                const { items, errors } = await parseAndValidateCsv(data);
                onUploadCsv(items, errors);
                setErrors(createRowErrorsObject(errors));
                setParsedItems(items);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                onUploadCsv([], [], true);
                setIsInvalidTemplate(true);
                setErrors(null);
                setParsedItems([]);
            }
            setShowCsvParseResults(true);
        };
        reader.readAsText(csvFile);
    }, [
        csvFile
    ]);
    function getUploadedItemCounts(items) {
        let formatted = '';
        if (!getItemTypeCounts) {
            formatted = "".concat(items.length, " ").concat(pluralize(entityName, items.length));
        } else {
            const counts = getItemTypeCounts(items);
            const entries = Object.entries(counts);
            if (entries.length === 0) return formatted;
            if (entries.length === 1) {
                const [entityName, count] = entries[0];
                formatted = "".concat(count, " ").concat(pluralize(entityName, count));
            } else if (entries.length > 1) {
                const lastEntry = entries.pop();
                formatted = entries.map((param)=>{
                    let [entityName, count] = param;
                    return "".concat(count, " ").concat(pluralize(entityName, count));
                }).join(', ');
                formatted += " and ".concat(lastEntry[1], " ").concat(pluralize(lastEntry[0], lastEntry[1]));
            }
        }
        return "We have found ".concat(formatted);
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        children: [
            csvTemplateUrl && /*#__PURE__*/ _jsx(ImportCard, {
                title: "1. Download the CSV file template",
                subtitle: "Click the button to download a template you can open with Excel or Google Sheets.",
                actionsStart: /*#__PURE__*/ _jsx(Button, {
                    href: csvTemplateUrl,
                    as: "a",
                    onClick: ()=>onEvent && onEvent('csvTemplateDownload'),
                    size: "small",
                    testId: testId && testId + '-download-example',
                    download: (()=>{
                        const arr = csvTemplateUrl.split('/');
                        return arr[arr.length - 1];
                    })(),
                    children: "Download template"
                })
            }),
            /*#__PURE__*/ _jsx(ImportCard, {
                title: "".concat(csvTemplateUrl ? '2. ' : '', "Upload your data"),
                subtitle: "Enter your data into the CSV template, and then upload it.",
                actionsStart: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("input", {
                            type: "file",
                            accept: ".csv",
                            className: styles.uploadCsvInput,
                            value: "",
                            "data-testid": testId && testId + '-upload-csv-input',
                            ref: csvInputRef,
                            onChange: (e)=>{
                                var _files_length;
                                var _e_target;
                                const files = (_e_target = e.target) === null || _e_target === void 0 ? void 0 : _e_target.files;
                                if (!files) {
                                    return;
                                }
                                if ((_files_length = files.length) !== null && _files_length !== void 0 ? _files_length : 0 > 0) {
                                    const file = files[0];
                                    setCsvFile(file);
                                }
                            }
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            size: "small",
                            disabled: !!csvFile,
                            testId: testId && testId + '-upload-csv-btn',
                            onClick: ()=>csvInputRef.current && csvInputRef.current.click(),
                            children: "Upload CSV file"
                        }),
                        csvFile && /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            children: csvFile.name
                        })
                    ]
                })
            }),
            showCsvParseResults && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ImportCard, {
                        title: "Results",
                        children: isCsvValid() ? /*#__PURE__*/ _jsxs(SuccessBox, {
                            testId: testId && testId + '-upload-success',
                            children: [
                                "File uploaded successfully! ",
                                getUploadedItemCounts(parsedItems),
                                "."
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: "File uploaded with errors. Please correct the errors and upload the file again."
                                }),
                                /*#__PURE__*/ _jsxs(ErrorBox, {
                                    className: styles.parseResults,
                                    testId: testId && testId + '-parse-results',
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.uploadErrorSummary,
                                            children: isInvalidTemplate ? "The CSV you have tried to upload isn't in the required format. Please download the CSV template and enter your data in accordance with the example." : "Found errors in ".concat((_Object_keys = Object.keys(errors || {})) === null || _Object_keys === void 0 ? void 0 : _Object_keys.length, " rows!")
                                        }),
                                        errors && Object.keys(errors).map((rowNumber, index)=>/*#__PURE__*/ _jsxs("div", {
                                                className: styles.errorRow,
                                                "data-testid": testId && testId + "-csv-error-row-".concat(rowNumber),
                                                children: [
                                                    /*#__PURE__*/ _jsx(Typography, {
                                                        weight: "normal",
                                                        children: "Row ".concat(rowNumber)
                                                    }),
                                                    errors[rowNumber].map((errorMsg, idx)=>/*#__PURE__*/ _jsx("div", {
                                                            className: styles.uploadErrorItemMessage,
                                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                                dangerouslySetInnerHTML: {
                                                                    __html: errorMsg
                                                                }
                                                            })
                                                        }, "specific-error-".concat(idx)))
                                                ]
                                            }, "error-row-".concat(index)))
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.actionButtons,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                skin: "primaryOutline",
                                testId: testId && testId + '-reset',
                                onClick: ()=>{
                                    setShowCsvParseResults(false);
                                    setCsvFile(null);
                                },
                                children: "Reset"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                disabled: !isCsvValid(),
                                testId: testId && testId + '-start-import',
                                onClick: ()=>onStartImportItems(parsedItems),
                                children: importBtnText
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
